// components/assign-location-modal/AssignLocationModalV2.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Kbd,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Spinner,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Tooltip,
    VStack,
    Badge,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { getUserData } from "utils";
import { createAPIEndPoint } from "config/api/api";
import toast from "react-hot-toast";
import { toProperCase } from "utils/formatting";

const AssignLocationModalV2 = ({
    isOpen,
    onClose,
    isLocationId = "",
    fetchAgain = () => { },
    preselectedMemberId,            // ✅ NEW
    preselectedMemberName = "",     // ✅ NEW (optional, for display)
}) => {
    const userData = getUserData();
    const clinic_id = userData?.clinic_id ?? null;

    // data
    const [teamMembers, setTeamMembers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [assignedLocationIds, setAssignedLocationIds] = useState(new Set()); // ✅ NEW

    // selection
    const [selectedMember, setSelectedMember] = useState("");
    const [selectedLocations, setSelectedLocations] = useState([]);

    // ui state
    const [loading, setLoading] = useState(false);
    const [loadingLists, setLoadingLists] = useState(false);

    // search state
    const [memberQuery, setMemberQuery] = useState("");
    const [locQuery, setLocQuery] = useState("");


    const fetchAssignedLocations = async (memberId) => {
        try {
            const res = await createAPIEndPoint(
                `clinic_team/staff/locations/${memberId}`
            ).fetchAll();
            const staffLocs = res?.data?.staff_locations || [];
            const ids = staffLocs.map((s) => Number(s.location_id));
            setAssignedLocationIds(new Set(ids));
        } catch (err) {
            // Don’t block the UI if this fails—just show all
            setAssignedLocationIds(new Set());
            toast.error(err?.response?.data?.error || "Failed to fetch assigned locations");
        }
    };


    // load when opened
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            setLoadingLists(true);
            try {
                // If member is preselected, no need to fetch members list
                await Promise.all([
                    preselectedMemberId ? Promise.resolve() : fetchTeamMembers(),
                    isLocationId ? Promise.resolve() : fetchLocations(),
                    preselectedMemberId ? fetchAssignedLocations(Number(preselectedMemberId)) : Promise.resolve(),
                ]);

                // ✅ Auto-select member if provided by parent
                if (preselectedMemberId) {
                    setSelectedMember(Number(preselectedMemberId));
                }
            } finally {
                setLoadingLists(false);
            }
        })();
    }, [isOpen, preselectedMemberId, isLocationId]);


    useEffect(() => {
        if (!isOpen) return;
        if (!isLocationId && selectedMember && !preselectedMemberId) {
            setAssignedLocationIds(new Set());
            fetchAssignedLocations(Number(selectedMember));
            // Optional: clear any previously selected (from another member)
            setSelectedLocations([]);
        }
    }, [selectedMember, isOpen, isLocationId, preselectedMemberId]);


    const fetchTeamMembers = async () => {
        try {
            const res = await createAPIEndPoint(
                `clinic_team/get_all/${clinic_id}`
            ).fetchAll();
            const members = res?.data?.team_members || [];

            // ✅ filter only staff roles
            const staffMembers = members.filter(
                (m) => m.clinic_role?.toLowerCase() === "staff"
            );

            setTeamMembers(
                staffMembers.map((m) => ({
                    id: m.user_id,
                    name: `${m.first_name} ${m.last_name}`.trim(),
                }))
            );
        } catch (err) {
            toast.error(
                err?.response?.data?.error || "Failed to fetch team members"
            );
        }
    };

    const fetchLocations = async () => {
        try {
            const res = await createAPIEndPoint(
                `clinic_locations/get_all/${clinic_id}`
            ).fetchAll();
            const locs = res?.data?.locations || [];
            setLocations(
                locs.map((l) => ({
                    id: l.id,
                    city: l.city || "",
                    state: l.state || "",
                    location_name: l.location_name || "",
                    label: `${l.location_name} (${l.city || "—"}, ${l.state || "—"})`,
                }))
            );
        } catch (err) {
            toast.error(
                err?.response?.data?.error || "Failed to fetch locations"
            );
        }
    };

    // filters
    const filteredMembers = useMemo(() => {
        const q = memberQuery.trim().toLowerCase();
        if (!q) return teamMembers;
        return teamMembers.filter((m) => m.name.toLowerCase().includes(q));
    }, [teamMembers, memberQuery]);

    const filteredLocations = useMemo(() => {
        if (isLocationId) return [];
        const q = locQuery.trim().toLowerCase();
        const searchMatch = (l) =>
            l.label.toLowerCase().includes(q) ||
            l.city.toLowerCase().includes(q) ||
            l.state.toLowerCase().includes(q);
        // ✅ Hide already-assigned
        return locations.filter(
            (l) => !assignedLocationIds.has(Number(l.id)) && (!q || searchMatch(l))
        );
    }, [locations, locQuery, isLocationId, assignedLocationIds]);

    const toggleLocation = (id) => {
        setSelectedLocations((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const selectAllVisible = () => {
        const ids = filteredLocations.map((l) => l.id);
        setSelectedLocations((prev) => Array.from(new Set([...prev, ...ids])));
    };

    const clearAllLocations = () => setSelectedLocations([]);

    const assignLocations = async () => {
        setLoading(true);
        const payload = {
            clinic_id,
            staff_id: selectedMember,
            location_ids: !!isLocationId ? [isLocationId] : selectedLocations,
        };
        try {
            await createAPIEndPoint("clinic_team/assign-location").create(payload);
            toast.success("Locations assigned successfully!");

            if (isLocationId || preselectedMemberId) {
                await Promise.resolve(fetchAgain?.());
            }

            // reset only when member is NOT preselected
            if (!preselectedMemberId) {
                setSelectedMember("");
                setMemberQuery("");
            }
            setSelectedLocations([]);
            setLocQuery("");
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.error || "Failed to assign locations");
        } finally {
            setLoading(false);
        }
    };

    // const assignLocations = async () => {
    //     setLoading(true);
    //     const payload = {
    //         clinic_id,
    //         staff_id: parseInt(selectedMember, 10),
    //         location_ids: !!isLocationId ? [isLocationId] : selectedLocations,
    //     };
    //     try {
    //         await createAPIEndPoint("clinic_team/assign-location").create(payload);
    //         toast.success("Locations assigned successfully!");

    //         if (isLocationId) fetchAgain();

    //         // reset
    //         setSelectedMember("");
    //         setSelectedLocations([]);
    //         setMemberQuery("");
    //         setLocQuery("");
    //         onClose?.();
    //     } catch (err) {
    //         toast.error(
    //             err?.response?.data?.error || "Failed to assign locations"
    //         );
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const SelectedChips = () => {
        if (!!isLocationId) return null;
        const map = new Map(locations.map((l) => [l.id, l]));
        const chips = selectedLocations.map((id) => map.get(id)).filter(Boolean);
        if (!chips.length) return null;

        const head = chips.slice(0, 20);
        const rest = chips.length - head.length;

        return (
            <HStack wrap="wrap" spacing={2} mt={2} gap={1}>
                {head.map((l) => (
                    <Tag
                        key={l.id}
                        size="sm"
                        colorScheme="blue"
                        borderRadius="full"
                        variant="subtle"
                        ml="0px !important"
                    >
                        <Tooltip label={toProperCase(l.label)} hasArrow>
                            <TagLabel>{toProperCase(l.location_name)}</TagLabel>
                        </Tooltip>
                        <TagCloseButton onClick={() => toggleLocation(l.id)} />
                    </Tag>
                ))}
                {rest > 0 && (
                    <Badge colorScheme="blue" borderRadius="full">
                        +{rest} more
                    </Badge>
                )}
                <IconButton
                    aria-label="Clear all"
                    icon={<CloseIcon boxSize={2.5} />}
                    size="xs"
                    variant="ghost"
                    onClick={clearAllLocations}
                    title="Clear all"
                />
            </HStack>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="16px" pb="16px" maxWidth="500px">
                <ModalHeader color="brand.500">
                    {/* Assign Locations to Team Members */}
                    Assign Locations
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody maxHeight="70vh" overflowY="auto" pb={2}>
                    {/* Member picker (single) */}
                    {/* <FormControl mb={4}>
                        <FormLabel>Team Member</FormLabel>
                        <Box
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            p={2}
                            _focusWithin={{
                                borderColor: "brand.500",
                                boxShadow:
                                    "0 0 0 1px var(--chakra-colors-brand-500)",
                            }}
                        >
                            <Input
                                placeholder="Search and choose a team member…"
                                value={memberQuery}
                                onChange={(e) => setMemberQuery(e.target.value)}
                                size="sm"
                            />
                            <Box maxH="180px" overflowY="auto" mt={2}>
                                {loadingLists ? (
                                    <HStack py={4} justify="center" color="gray.500">
                                        <Spinner size="sm" />{" "}
                                        <Text fontSize="sm">Loading members…</Text>
                                    </HStack>
                                ) : (
                                    <VStack align="stretch" spacing={1}>
                                        {filteredMembers.map((m) => {
                                            const checked =
                                                String(selectedMember) === String(m.id);
                                            return (
                                                <Box
                                                    key={m.id}
                                                    px={2}
                                                    py={2}
                                                    borderRadius="md"
                                                    _hover={{ bg: "gray.50" }}
                                                    bg={checked ? "blue.50" : "transparent"}
                                                    cursor="pointer"
                                                    onClick={() => setSelectedMember(m.id)}
                                                >
                                                    <HStack justify="space-between">
                                                        <Text fontSize="sm">{toProperCase(m.name)}</Text>
                                                        {checked && (
                                                            <Badge colorScheme="blue" textTransform="capitalize">Selected</Badge>
                                                        )}
                                                    </HStack>
                                                </Box>
                                            );
                                        })}
                                        {!filteredMembers.length && (
                                            <Text fontSize="sm" color="gray.500" py={2}>
                                                No results. Try another name.
                                            </Text>
                                        )}
                                    </VStack>
                                )}
                            </Box>
                        </Box>
                    </FormControl> */}

                    {preselectedMemberId ? (
                        <Box
                            mb={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            p={3}
                            bg="gray.50"
                        >
                            <Text fontSize="xs" color="gray.600">Team Member</Text>
                            <Text fontWeight="semibold" >
                                {toProperCase(preselectedMemberName) || `ID: ${preselectedMemberId}`}
                            </Text>
                            {assignedLocationIds.size > 0 && (
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    {assignedLocationIds.size} assigned location{assignedLocationIds.size > 1 ? 's' : ''} hidden.
                                </Text>
                            )}
                        </Box>
                    ) : (
                        // 🔽 Original member picker only when NOT preselected
                        <FormControl mb={4}>
                            <FormLabel>Team Member</FormLabel>
                            <Box
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={2}
                                _focusWithin={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                                }}
                            >
                                <Input
                                    placeholder="Search and choose a team member…"
                                    value={memberQuery}
                                    onChange={(e) => setMemberQuery(e.target.value)}
                                    size="sm"
                                />
                                <Box maxH="180px" overflowY="auto" mt={2}>
                                    {loadingLists ? (
                                        <HStack py={4} justify="center" color="gray.500">
                                            <Spinner size="sm" />{" "}
                                            <Text fontSize="sm">Loading members…</Text>
                                        </HStack>
                                    ) : (
                                        <VStack align="stretch" spacing={1}>
                                            {filteredMembers.map((m) => {
                                                const checked = String(selectedMember) === String(m.id);
                                                return (
                                                    <Box
                                                        key={m.id}
                                                        px={2}
                                                        py={2}
                                                        borderRadius="md"
                                                        _hover={{ bg: "gray.50" }}
                                                        bg={checked ? "blue.50" : "transparent"}
                                                        cursor="pointer"
                                                        onClick={() => setSelectedMember(m.id)}
                                                    >
                                                        <HStack justify="space-between">
                                                            <Text fontSize="sm">{toProperCase(m.name)}</Text>
                                                            {checked && (
                                                                <Badge colorScheme="blue" textTransform="capitalize">
                                                                    Selected
                                                                </Badge>
                                                            )}
                                                        </HStack>
                                                    </Box>
                                                );
                                            })}
                                            {!filteredMembers.length && (
                                                <Text fontSize="sm" color="gray.500" py={2}>
                                                    No results. Try another name.
                                                </Text>
                                            )}
                                        </VStack>
                                    )}
                                </Box>
                            </Box>
                        </FormControl>
                    )}


                    {/* Locations picker (multi) — same look/feel as member list */}
                    {!isLocationId && (
                        <FormControl>
                            <FormLabel>Locations</FormLabel>
                            <Box
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={2}
                                _focusWithin={{
                                    borderColor: "brand.500",
                                    boxShadow:
                                        "0 0 0 1px var(--chakra-colors-brand-500)",
                                }}
                            >
                                <HStack mb={2} justify="space-between">
                                    <Input
                                        placeholder="Search by name, city, or state…"
                                        value={locQuery}
                                        onChange={(e) => setLocQuery(e.target.value)}
                                        size="sm"
                                    />
                                    <HStack>
                                        {/* <Button size="xs" variant="ghost" onClick={selectAllVisible}>
                                            Select visible
                                        </Button> */}
                                        <Button size="xs" variant="outline" colorScheme="red" color="red.500" borderRadius="8px" minHeight={"32px"} onClick={clearAllLocations}>
                                            Clear
                                        </Button>
                                    </HStack>
                                </HStack>

                                <Box maxH="20dvh" minH="200px" overflowY="auto" mt={1}>
                                    {loadingLists ? (
                                        <HStack py={4} justify="center" color="gray.500">
                                            <Spinner size="sm" />{" "}
                                            <Text fontSize="sm">Loading locations…</Text>
                                        </HStack>
                                    ) : filteredLocations.length ? (
                                        <VStack align="stretch" spacing={1}>
                                            {filteredLocations.map((l) => {
                                                const checked = selectedLocations.includes(l.id);
                                                return (
                                                    <HStack
                                                        key={l.id}
                                                        px={2}
                                                        py={2}
                                                        borderRadius="md"
                                                        _hover={{ bg: "gray.50" }}
                                                        bg={checked ? "green.50" : "transparent"}
                                                        cursor="pointer"
                                                        onClick={() => toggleLocation(l.id)}
                                                        align="flex-start"
                                                    >
                                                        <Checkbox
                                                            isChecked={checked}
                                                            onChange={() => toggleLocation(l.id)}
                                                            pointerEvents="none"
                                                            mt="2px"
                                                        />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="medium">
                                                                {l.location_name}
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.600">
                                                                {toProperCase(l.city) || "—"}, {toProperCase(l.state) || "—"}
                                                            </Text>
                                                        </Box>
                                                        {/* <Badge ml="auto" variant="subtle">
                                                            {l.id}
                                                        </Badge> */}
                                                    </HStack>
                                                );
                                            })}
                                        </VStack>
                                    ) : (
                                        <Text fontSize="sm" color="gray.500" py={2}>
                                            {assignedLocationIds.size > 0
                                                ? "All matching locations are already assigned."
                                                : "No locations match your search."}                                        </Text>
                                    )}
                                </Box>

                                <Divider my={3} />
                                <HStack justify="space-between">
                                    <Text fontSize="sm" color="gray.600">
                                        {selectedLocations.length} selected
                                    </Text>
                                    {/* <Button size="sm" onClick={() => { }} colorScheme="blue" variant="solid">
                                        Done
                                    </Button> */}
                                </HStack>
                            </Box>

                            {/* Chips preview */}
                            <SelectedChips />
                        </FormControl>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button
                        w="100%"
                        colorScheme="brand"
                        onClick={assignLocations}
                        isDisabled={
                            !selectedMember || (!isLocationId && selectedLocations.length === 0)
                        }
                        isLoading={loading}
                    >
                        Assign
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AssignLocationModalV2;
